import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'

import { useToastContext } from '../../../../../contexts/toast'
import { AllowanceCharge } from '../../../../../data/models'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import {
  requestPutAllowanceChargesById,
  useInvoiceAllowanceChargeById,
} from '../../../data'

type Props = {
  invoiceId: string
  id: string
  formik: FormikProps<any>
}

export const ResetAllowanceCharge: React.FC<Props> = ({
  invoiceId,
  id,
  formik,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()
  const { data: invoiceAllowanceCharge } = useInvoiceAllowanceChargeById(
    invoiceId,
    id,
  )

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  async function handleSubmit() {
    // deactivate the button
    setIsRequestRunning(true)

    try {
      // handleReset
      handleReset()

      // show success toast
      showToast({
        closable: true,
        severity: 'success',
        summary: t(
          'invoice.invoiceItems.table.edit.dialog.allowanceCharges.table.edit.dialog.reset.messages.success.summary',
          {
            type:
              invoiceAllowanceCharge?.ConfirmationAlcType === 'Surcharge'
                ? t('common.codes.alcType.surcharge')
                : t('common.codes.alcType.discount'),
            description: invoiceAllowanceCharge?.ConfirmationDescription,
          },
        ),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        closable: true,
        severity: 'error',
        sticky: true,
        summary: errorMessage,
      })
    } finally {
      // activate the button
      setIsRequestRunning(false)
    }
  }

  // handleReset
  const handleReset = async () => {
    formik.resetForm()

    const resetData: AllowanceCharge = produce(
      invoiceAllowanceCharge!,
      draft => {
        // visible fields
        draft.ConfirmationPercentage = invoiceAllowanceCharge?.Percentage
        draft.ConfirmationAmount = invoiceAllowanceCharge?.Amount

        // hidden fields
        draft.Status = 'NOT_SET'
      },
    )

    // put for allowance charge
    requestPutAllowanceChargesById(invoiceId, id, resetData)
  }

  return (
    <Button
      className="p-button-outlined w-full md:w-auto"
      label={t('common.buttons.reset')}
      onClick={handleSubmit}
      icon="pi pi-replay"
      loading={isRequestRunning}
      type="reset"
    />
  )
}
